// Pure helpers for the insurance-duplicate-policy decide flow (P1-5,
// cleanup UX audit 2026-07-13). The audit found Joy's 50 policy-conflict
// items rendered only the household name — she had to open the record,
// find Policies & Services, and hunt for the two rows herself (~8
// clicks). The detector already emits one `affectedRecords` entry per
// conflicting policy row with a full comparison string as its `label`
// (carrier · type · status · premium · expiration · source — see
// rowDetail() in shared/db/cleanup-detectors/insurance-duplicate-policy.ts),
// so the rail can render a side-by-side comparison with zero extra
// fetch. This module just picks the rows apart and writes the
// plain-English consequence sentence for whichever row is selected.
//
// Same pattern as client-since-issue.ts / expiration-issue.ts:
// root-safe (no imports) so it's unit-tested from tests/web/.

export interface DuplicatePolicyIssueLike {
  detectorSlug: string;
  affectedRecords: Array<{ kind: string; id: string; label: string }>;
}

export function isDuplicatePolicyIssue(
  issue: DuplicatePolicyIssueLike,
): boolean {
  return issue.detectorSlug === "insurance-duplicate-policy";
}

export interface DuplicatePolicyRow {
  id: string;
  /** "Home · Progressive · active · $1,200/yr · exp 2027-03-01 · carrier_import" */
  detail: string;
}

// Every affected record on this detector IS a policy row (see the
// detector's comment: "ONLY the policy service_records are affected
// records"), so no kind filter is needed — unlike contact/household
// clusters where affectedRecords can mix kinds.
export function duplicatePolicyRows(
  issue: DuplicatePolicyIssueLike,
): DuplicatePolicyRow[] {
  if (!isDuplicatePolicyIssue(issue)) return [];
  return issue.affectedRecords.map((r) => ({ id: r.id, detail: r.label }));
}

// Plain-English before→after for whichever row is currently selected
// as the survivor (UX rule #3). `survivorId` is null until the steward
// picks one or the detector's recommended default is known.
export function duplicatePolicyConsequence(
  rows: DuplicatePolicyRow[],
  survivorId: string | null,
): string {
  if (rows.length < 2) return "";
  const survivor = rows.find((r) => r.id === survivorId);
  const others = rows.filter((r) => r.id !== survivorId);
  if (!survivor) {
    return "Pick which row is the real policy — check the carrier portal if you're not sure. The other row's history folds into the one you keep; nothing is lost. Reversible for 60 seconds and from History.";
  }
  return `Keeps "${survivor.detail}" as the real policy and folds ${
    others.length === 1 ? "the other row" : `the other ${others.length} rows`
  } into it — their history carries over, nothing is lost. Reversible for 60 seconds and from History.`;
}
